import React, { useState, useEffect } from 'react';

import { Skeleton } from '@material-ui/lab';
import ClickToScrollNavBar from 'components/ClickToScrollNavBar';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import SidepanelFooter from 'screens/admin/common/sidepanelFooter';
import SidepanelHeader from 'screens/admin/common/sidepanelHeader';
import { selectMSTeamsContext } from 'selectors/msTeamsSelector';
import styled from 'styled-components';
import { MarginContainer, StyledBox, StyledDivider, StyledTypography } from 'styles/common';

import { FEEDBACK_FORM_TYPE } from 'constants/ProjectConstants';
import { pushFeedbackParams } from 'constants/helper';

import { getAllRequestedFeedbackList } from 'services/continuousFeedback';
import { showErrorSnackbar } from 'services/snackbar';

import FeedbackListRowView from './common/FeedbackListRowView';

// Lists all feedbacks Requested/Shared
//
// We hide the footer in MSTeams View.
// Reason being, we are already providing Request/Share view in MSTeams Personal Tab.
// The actions on footer opens forms for Sharing/Requesting feedback. We want to avoid providing
// Multiple viewes for same feature as it may be quite confusing and inconsistent.

const Container = styled(StyledBox)`
  position: relative;
  height: 100%;
`;
const StyledClickToScrollNavBar = styled(ClickToScrollNavBar)`
  margin: 1rem 2rem;
`;

const StyledSkeleton = styled(Skeleton)`
  margin-top: 1rem;
`;

const NavItemHeader = () => <header>Requested</header>;

const NavItemContainer =
  ({ requestedFeedback, openFeedbackDetails }) =>
  () => (
    <MarginContainer topGutter={2}>
      <StyledTypography variant="body1" weight="bold">
        All requested feedback
      </StyledTypography>
      {requestedFeedback === null || requestedFeedback === undefined ? null : (
        <>
          {requestedFeedback.map((item) => (
            <FeedbackListRowView
              key={item.review.id}
              employee={item.employee}
              review={item.review}
              handleClick={openFeedbackDetails}
              type={FEEDBACK_FORM_TYPE.REQUESTED}
            />
          ))}
        </>
      )}
    </MarginContainer>
  );

const FeedbackListSidepanel = ({ handleClose, handleOpenShareForm, handleOpenRequestForm, defaultTab }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [loading, setLoading] = useState(null);
  const [requestedFeedback, setRequestedFeedback] = useState(null);

  const msTeamsContext = useSelector(selectMSTeamsContext);
  const isMsTeamsView = Boolean(msTeamsContext);

  const openFeedbackDetails = ({ formType, feedbackId }) => {
    const show = true;
    pushFeedbackParams({ history, formType, feedbackId, show });
  };

  const getData = () => {
    setLoading(true);
    getAllRequestedFeedbackList()
      .then((res) => setRequestedFeedback(res))
      .catch(() => dispatch(showErrorSnackbar('Something went wrong!')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getData();
  }, []);

  const FeedbackListNavItems = [
    {
      id: 'requested',
      header: NavItemHeader,
      component: NavItemContainer({ requestedFeedback, openFeedbackDetails }),
    },
  ];

  return (
    <Container flexcontainer flexdirection="column">
      <SidepanelHeader text="All requested feedback" fontWeight={600} onCancel={handleClose} loading={loading} />
      <StyledDivider />
      {loading === false ? (
        <StyledClickToScrollNavBar
          sectionList={FeedbackListNavItems}
          loading={loading}
          showDividers
          defaultSelectedIndex={defaultTab || 0}
        />
      ) : (
        <Container flexcontainer flexdirection="column" topGutter={2} bottomGutter={2} padding="0 2rem">
          {Array(5)
            .fill(null)
            .map((item, index) => (
              <StyledSkeleton
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                component="div"
                width="100%"
                height="5%"
                animation="wave"
                variant="rect"
              />
            ))}
        </Container>
      )}
      {!isMsTeamsView && (
        <SidepanelFooter
          onSave={handleOpenRequestForm}
          onCancel={handleOpenShareForm}
          loading={loading}
          primaryActionText="Request"
          secondaryActionText="Share"
        />
      )}
    </Container>
  );
};

export default FeedbackListSidepanel;
